import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	GestureResponderEvent,
	Image,
	ImageSourcePropType,
	StyleSheet,
	Text,
	View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DuelChipStackPng,
	DuelGiftBoxPng,
	DuelIconChevronSvg,
	DuelIconLockSvg,
	DuelNodeCheckSvg,
	DuelRealMoneyPng,
	DuelSeasonBannerJpg,
	DuelSeasonGiftPng,
	DuelXpJpg,
} from '../../assets/duels';
import type {
	DuelPlayerRewardDto,
	DuelRewardStatus,
	DuelRewardType,
	DuelSeasonMilestoneDto,
} from '../../services/api';
import type { RootState } from '../../store';
import {
	duelRewardClaimErrorCleared,
	duelRewardClaimSubmit,
	duelsHubRequest,
	duelsRewardsRequest,
	duelsSeasonRequest,
} from '../../store/duels/duelsActions';
import {
	selectDuelClaimErrorKey,
	selectDuelClaimingRewardIds,
	selectDuelRewards,
	selectDuelSeasonErrorKey,
	selectDuelSeasonProgress,
} from '../../store/duels/duelsReducer';
import { CtaButton } from '../Components/common/CtaButton';
import { GoldGradientText } from '../Components/common/GoldGradientText';
import { TouchableOpacity } from '../Components/common/Touchable';
import {
	asRewardStatus,
	DuelRewardStatusPill,
	rewardStatusText,
	rewardTitle,
} from '../Components/Duels/DuelRewardUi';
import { DuelBanner } from '../Components/Duels/DuelStatCards';
import { duelText } from '../Components/Duels/duelsText';
import { daysUntil, duelColors, formatScore } from '../Components/Duels/duelsTheme';
import {
	DuelButton,
	DuelClockText,
	DuelLoading,
	DuelNotice,
	DuelPanel,
	DuelProgressBar,
	DuelsPage,
	duelTextStyles,
} from '../Components/Duels/DuelsUi';
import { DailyMissionClaimAnimation } from '../Components/Modals/DailyMissionsComponents/DailyMissionClaimAnimation';
import { RootStackParamList } from '../navigator';
import { getWalletPoint } from '../shell/header/walletPosition';

type NavType = NativeStackNavigationProp<RootStackParamList>;

const REWARD_ICONS: Record<DuelRewardType, ImageSourcePropType> = {
	Chips: DuelChipStackPng,
	Experience: DuelXpJpg,
	VipDays: DuelGiftBoxPng,
	RealWorld: DuelRealMoneyPng,
};

const TIMELINE_GUTTER = 26;
const LOCKED_PREVIEW = 2;
const SUMMARY_THUMBNAILS = 3;
const BANNER_ASPECT = 1080 / 432;
const DONE_STATUSES: DuelRewardStatus[] = ['Granted', 'Fulfilled'];
const BANNER_LINE_CHARS = 14;

const bannerLines = (name: string) => {
	if (name.length <= BANNER_LINE_CHARS) return [name];
	let best: [string, string] | null = null;
	for (let index = name.indexOf(' '); index > 0; index = name.indexOf(' ', index + 1)) {
		const pair: [string, string] = [name.slice(0, index), name.slice(index + 1)];
		const longest = Math.max(pair[0].length, pair[1].length);
		if (!best || longest < Math.max(best[0].length, best[1].length)) best = pair;
	}
	return best ?? [name];
};

type Entry = {
	milestone: DuelSeasonMilestoneDto;
	reward: DuelPlayerRewardDto | undefined;
	status: DuelRewardStatus | null;
};

const needsAction = (entry: Entry) => !!entry.reward?.canClaim || !!entry.reward?.canRequest;

const TimelineNode = ({ entry, scale }: { entry: Entry; scale: number }) => {
	const size = 18 * scale;
	if (!entry.milestone.reached) {
		return (
			<View style={[styles.node, { width: size, height: size, borderRadius: size / 2 }]}>
				<DuelIconLockSvg width={11 * scale} height={11 * scale} />
			</View>
		);
	}
	if (needsAction(entry)) {
		return (
			<Image source={DuelGiftBoxPng} style={{ width: size, height: size }} resizeMode='contain' />
		);
	}
	return <DuelNodeCheckSvg width={size} height={size} />;
};

type RowProps = {
	entry: Entry;
	width: number;
	scale: number;
	claiming: boolean;
	onClaim: (entitlementId: string, amount: number, source: { x: number; y: number }) => void;
	onOpenReward: (entitlementId: string) => void;
};

const MilestoneRow = ({ entry, width, scale, claiming, onClaim, onOpenReward }: RowProps) => {
	const { t } = useTranslation();
	const { milestone, reward, status } = entry;
	const realWorld = milestone.rewardType === 'RealWorld';

	const statusText = (key: string, color: string) => (
		<Text style={[duelTextStyles.title, { fontSize: 11 * scale, color }]}>{duelText(t, key)}</Text>
	);

	const trailing = () => {
		if (!milestone.reached) {
			return <DuelIconLockSvg width={20 * scale} height={20 * scale} />;
		}
		if (reward?.canClaim) {
			return (
				<CtaButton
					label={duelText(t, 'T_SITE_DUELS_CLAIM')}
					onPress={(event: GestureResponderEvent) => {
						const { pageX, pageY } = event.nativeEvent;
						onClaim(reward.id, milestone.value * milestone.quantity, { x: pageX, y: pageY });
					}}
					busy={claiming}
					style={{ width: 92 * scale, height: 28 * scale }}
					surfaceStyle={{ minHeight: 28 * scale }}
					labelStyle={{ fontSize: 13 * scale }}
				/>
			);
		}
		if (realWorld) {
			if (reward?.canRequest) {
				return (
					<DuelButton
						label={duelText(t, 'T_SITE_DUELS_REQUEST')}
						onPress={() => onOpenReward(reward.id)}
						width={92 * scale}
						labelSize={12}
						scale={scale}
					/>
				);
			}
			if (!status) return null;
			const pill = (
				<DuelRewardStatusPill status={status} scale={scale} label={rewardStatusText(t, status)} />
			);
			return reward ? (
				<TouchableOpacity onPress={() => onOpenReward(reward.id)} accessibilityRole='button'>
					{pill}
				</TouchableOpacity>
			) : (
				pill
			);
		}
		if (status === 'Granted') {
			return (
				<Text
					style={[
						duelTextStyles.title,
						styles.centerText,
						{ fontSize: 10 * scale, color: duelColors.positive },
					]}
				>
					{`✓\n${duelText(t, 'T_SITE_DUELS_CLAIMED')}`}
				</Text>
			);
		}
		if (status === 'GrantFailed') {
			return statusText('T_SITE_DUELS_STATUS_CONTACT_SUPPORT', duelColors.negative);
		}
		if (status === 'Unlocked' && !reward) return null;
		return statusText('T_SITE_DUELS_STATUS_ON_ITS_WAY', duelColors.positive);
	};

	return (
		<DuelPanel scale={scale} width={width} highlighted={needsAction(entry)}>
			<View style={[styles.row, { columnGap: 10 * scale }]}>
				<Text
					style={[
						duelTextStyles.gold,
						{ width: 74 * scale, fontSize: 14 * scale },
						!milestone.reached && { color: duelColors.muted },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_POINTS', [formatScore(milestone.threshold)])}
				</Text>
				<Image
					source={REWARD_ICONS[milestone.rewardType]}
					style={[
						{ width: 32 * scale, height: 32 * scale, borderRadius: 6 * scale },
						!milestone.reached && styles.dimmed,
					]}
					resizeMode='contain'
				/>
				<Text
					numberOfLines={2}
					style={[
						duelTextStyles.body,
						styles.flex,
						{ fontSize: 13 * scale },
						!milestone.reached && { color: duelColors.muted },
					]}
				>
					{rewardTitle(t, milestone)}
				</Text>
				{trailing()}
			</View>
		</DuelPanel>
	);
};

type SummaryProps = {
	entries: Entry[];
	open: boolean;
	onToggle: () => void;
	width: number;
	scale: number;
};

const ClaimedSummary = ({ entries, open, onToggle, width, scale }: SummaryProps) => {
	const { t } = useTranslation();
	const first = entries[0].milestone.threshold;
	const last = entries[entries.length - 1].milestone.threshold;
	return (
		<TouchableOpacity onPress={onToggle} accessibilityRole='button'>
			<DuelPanel scale={scale} width={width}>
				<View style={[styles.row, { columnGap: 8 * scale }]}>
					<View style={styles.flex}>
						<Text style={[duelTextStyles.title, { fontSize: 14 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_REWARDS_CLAIMED', [entries.length])}
						</Text>
						<Text style={[duelTextStyles.muted, { fontSize: 11 * scale }]}>
							{`${formatScore(first)} – ${duelText(t, 'T_SITE_DUELS_POINTS', [formatScore(last)])}`}
						</Text>
					</View>
					{entries.slice(-SUMMARY_THUMBNAILS).map(entry => (
						<Image
							key={entry.milestone.id}
							source={REWARD_ICONS[entry.milestone.rewardType]}
							style={{ width: 26 * scale, height: 26 * scale }}
							resizeMode='contain'
						/>
					))}
					<View style={{ transform: [{ rotate: open ? '-90deg' : '90deg' }] }}>
						<DuelIconChevronSvg width={16 * scale} height={16 * scale} />
					</View>
				</View>
			</DuelPanel>
		</TouchableOpacity>
	);
};

const DuelSeasonScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const season = useSelector(selectDuelSeasonProgress);
	const errorKey = useSelector(selectDuelSeasonErrorKey);
	const rewards = useSelector(selectDuelRewards);
	const claimingIds = useSelector(selectDuelClaimingRewardIds);
	const claimErrorKey = useSelector(selectDuelClaimErrorKey);
	const chips = useSelector((state: RootState) => state.auth.user?.chips ?? 0);
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [claimAnimation, setClaimAnimation] = useState<{
		id: number;
		source: { x: number; y: number };
		wallet: { x: number; y: number; scale?: number };
		amount: number;
		initialChips: number;
	} | null>(null);

	useEffect(() => {
		if (!claimAnimation) return;

		const timer = setTimeout(() => setClaimAnimation(null), 1920);
		return () => clearTimeout(timer);
	}, [claimAnimation]);

	useFocusEffect(
		useCallback(() => {
			dispatch(duelRewardClaimErrorCleared());
			dispatch(duelsSeasonRequest());
			dispatch(duelsRewardsRequest());
			dispatch(duelsHubRequest());
		}, [dispatch]),
	);

	const entries = useMemo<Entry[]>(() => {
		const seasonId = season?.season?.id;
		const rewardByMilestone = new Map(
			rewards
				.filter(reward => reward.seasonId === seasonId)
				.map(reward => [reward.milestoneId, reward]),
		);
		return (season?.milestones ?? []).map(milestone => {
			const reward = rewardByMilestone.get(milestone.id);
			return {
				milestone,
				reward,
				status: reward?.status ?? asRewardStatus(milestone.entitlementStatus),
			};
		});
	}, [rewards, season]);

	const openReward = (entitlementId: string) =>
		navigation.navigate('DuelReward', { entitlementId });
	const claim = (entitlementId: string) => dispatch(duelRewardClaimSubmit(entitlementId));
	const claimCheckpoint = (
		entitlementId: string,
		amount: number,
		source: { x: number; y: number },
	) => {
		const reward = rewards.find(item => item.id === entitlementId);
		const wallet = getWalletPoint();
		if (reward?.rewardType === 'Chips' && wallet) {
			setClaimAnimation({ id: Date.now(), source, wallet, amount, initialChips: chips });
		}
		claim(entitlementId);
	};
	const openInventory = () =>
		navigation.navigate('Inventory', { tab: 'inventory', category: 'rewards' });

	return (
		<>
			<DuelsPage
				title={duelText(t, 'T_SITE_DUELS_SEASON_TITLE')}
				onBack={() => navigation.goBack()}
			>
				{({ width, scale }) => {
					if (!season) {
						return errorKey ? (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(t, errorKey)}
								actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
								onAction={() => dispatch(duelsSeasonRequest())}
							/>
						) : (
							<DuelLoading />
						);
					}

					const otherSeasonRequestable = rewards.some(
						reward => reward.canRequest && reward.seasonId !== season.season?.id,
					);
					const otherSeasonClaimable = rewards.filter(
						reward => reward.canClaim && reward.seasonId !== season.season?.id,
					);
					const claimableElsewhere = otherSeasonClaimable.length ? (
						<DuelPanel scale={scale} width={width} highlighted>
							<Text style={[duelTextStyles.title, { fontSize: 14 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_REWARDS_TO_CLAIM')}
							</Text>
							{otherSeasonClaimable.map(reward => (
								<View key={reward.id} style={[styles.row, { columnGap: 10 * scale }]}>
									<Image
										source={REWARD_ICONS[reward.rewardType]}
										style={{ width: 32 * scale, height: 32 * scale }}
										resizeMode='contain'
									/>
									<View style={styles.flex}>
										<Text numberOfLines={1} style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
											{rewardTitle(t, reward)}
										</Text>
										<Text
											numberOfLines={1}
											style={[duelTextStyles.muted, { fontSize: 11 * scale }]}
										>
											{reward.seasonName}
										</Text>
									</View>
									<CtaButton
										label={duelText(t, 'T_SITE_DUELS_CLAIM')}
										onPress={event => {
											const { pageX, pageY } = event.nativeEvent;
											claimCheckpoint(reward.id, reward.value, { x: pageX, y: pageY });
										}}
										busy={claimingIds.includes(reward.id)}
										style={{ width: 92 * scale, height: 28 * scale }}
										surfaceStyle={{ minHeight: 28 * scale }}
										labelStyle={{ fontSize: 13 * scale }}
									/>
								</View>
							))}
						</DuelPanel>
					) : null;
					const rewardsWaiting = otherSeasonRequestable ? (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(t, 'T_SITE_DUELS_REWARDS_WAITING')}
							actionLabel={duelText(t, 'T_SITE_DUELS_OPEN_REWARDS')}
							onAction={openInventory}
						/>
					) : null;

					if (!season.season) {
						return (
							<>
								<DuelNotice
									scale={scale}
									width={width}
									text={duelText(t, 'T_SITE_DUELS_NO_SEASON')}
								/>
								{claimableElsewhere}
								{rewardsWaiting}
							</>
						);
					}

					const top = season.milestones[season.milestones.length - 1]?.threshold ?? 0;
					const progress = top ? Math.min(1, season.points / top) : 0;
					const days = daysUntil(season.season.endsAt);

					const done = entries.filter(
						entry =>
							entry.milestone.reached && !!entry.status && DONE_STATUSES.includes(entry.status),
					);
					const open = entries.filter(entry => entry.milestone.reached && !done.includes(entry));
					const locked = entries.filter(entry => !entry.milestone.reached);
					const visible = [
						...(summaryOpen || showAll ? done : []),
						...open,
						...(showAll ? locked : locked.slice(0, LOCKED_PREVIEW)),
					];
					const rowWidth = width - TIMELINE_GUTTER * scale;

					return (
						<>
							<DuelBanner
								source={DuelSeasonBannerJpg}
								aspect={BANNER_ASPECT}
								width={width}
								scale={scale}
								textWidth={width * 0.5}
							>
								{bannerLines(season.season.name).map((line, index) => (
									<GoldGradientText
										key={index}
										text={line}
										width={width * 0.5 - 12 * scale}
										height={26 * scale}
										fontSize={(line.length > 12 ? 17 : 22) * scale}
										fontWeight='900'
									/>
								))}
								<DuelClockText
									text={
										days
											? duelText(t, 'T_SITE_DUELS_DAYS_LEFT', [days])
											: duelText(t, 'T_SITE_DUELS_ENDS_TODAY')
									}
									scale={scale}
									white
								/>
							</DuelBanner>
							<Text
								style={[duelTextStyles.body, styles.centerText, { width, fontSize: 12 * scale }]}
							>
								{duelText(t, 'T_SITE_DUELS_SEASON_TAGLINE')}
							</Text>

							<DuelPanel scale={scale} width={width}>
								<View style={[styles.row, styles.between]}>
									<View style={{ rowGap: 4 * scale }}>
										<DuelClockText
											text={duelText(t, 'T_SITE_DUELS_SEASON_POINTS')}
											scale={scale}
											fontSize={16}
											white
										/>
										<View style={[styles.row, { columnGap: 6 * scale }]}>
											<GoldGradientText
												text={formatScore(season.points)}
												width={150 * scale}
												height={38 * scale}
												fontSize={32 * scale}
												fontWeight='900'
												align='left'
											/>
											{top ? (
												<Text
													style={[duelTextStyles.title, { fontSize: 18 * scale }]}
												>{`/ ${formatScore(top)}`}</Text>
											) : null}
										</View>
									</View>
									<Image
										source={DuelSeasonGiftPng}
										style={{ width: 52 * scale, height: 54 * scale }}
										resizeMode='contain'
									/>
								</View>
								<DuelProgressBar value={progress} width={width - 24 * scale} scale={scale} />
								<View style={[styles.row, styles.between]}>
									<Text style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
										{season.nextMilestone
											? duelText(t, 'T_SITE_DUELS_NEXT_MILESTONE', [
													formatScore(season.nextMilestone.threshold),
												])
											: duelText(t, 'T_SITE_DUELS_ALL_MILESTONES')}
									</Text>
									<Text style={[duelTextStyles.gold, { fontSize: 11 * scale }]}>
										{duelText(t, 'T_SITE_DUELS_PERCENT_COMPLETED', [
											`${Math.round(progress * 100)}%`,
										])}
									</Text>
								</View>
							</DuelPanel>

							{claimableElsewhere}

							{claimErrorKey ? (
								<DuelNotice scale={scale} width={width} text={duelText(t, claimErrorKey)} />
							) : null}

							<View style={{ width, rowGap: 8 * scale }}>
								<View
									style={[
										styles.timelineLine,
										{
											left: (TIMELINE_GUTTER * scale) / 2 - 1,
											top: 20 * scale,
											bottom: 20 * scale,
										},
									]}
								/>
								{done.length ? (
									<View style={styles.row}>
										<View style={[styles.center, { width: TIMELINE_GUTTER * scale }]}>
											<DuelNodeCheckSvg width={18 * scale} height={18 * scale} />
										</View>
										<ClaimedSummary
											entries={done}
											open={summaryOpen || showAll}
											onToggle={() => {
												if (showAll) {
													setShowAll(false);
													setSummaryOpen(false);
												} else {
													setSummaryOpen(value => !value);
												}
											}}
											width={rowWidth}
											scale={scale}
										/>
									</View>
								) : null}
								{visible.map(entry => (
									<View key={entry.milestone.id} style={styles.row}>
										<View style={[styles.center, { width: TIMELINE_GUTTER * scale }]}>
											<TimelineNode entry={entry} scale={scale} />
										</View>
										<MilestoneRow
											entry={entry}
											width={rowWidth}
											scale={scale}
											claiming={!!entry.reward && claimingIds.includes(entry.reward.id)}
											onClaim={claimCheckpoint}
											onOpenReward={openReward}
										/>
									</View>
								))}
							</View>

							{locked.length > LOCKED_PREVIEW ? (
								<TouchableOpacity
									onPress={() => setShowAll(value => !value)}
									accessibilityRole='button'
									style={[styles.row, { columnGap: 6 * scale }]}
								>
									<Text style={[duelTextStyles.gold, { fontSize: 13 * scale }]}>
										{showAll
											? duelText(t, 'T_SITE_DUELS_SHOW_LESS')
											: duelText(t, 'T_SITE_DUELS_VIEW_ALL_REWARDS', [entries.length])}
									</Text>
									<View style={{ transform: [{ rotate: showAll ? '-90deg' : '0deg' }] }}>
										<DuelIconChevronSvg width={14 * scale} height={14 * scale} />
									</View>
								</TouchableOpacity>
							) : null}

							{rewardsWaiting}
						</>
					);
				}}
			</DuelsPage>
			<DailyMissionClaimAnimation request={claimAnimation} fullScreen />
		</>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	between: {
		justifyContent: 'space-between',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	dimmed: {
		opacity: 0.45,
	},
	center: {
		alignItems: 'center',
	},
	centerText: {
		textAlign: 'center',
	},
	node: {
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#2A0804',
		borderWidth: 1,
		borderColor: 'rgba(217,196,188,0.6)',
	},
	timelineLine: {
		position: 'absolute',
		width: 2,
		backgroundColor: 'rgba(255,215,102,0.45)',
	},
});

export default DuelSeasonScreen;
